import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Rocket,
  Globe,
  ExternalLink,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  CheckCircle,
  Link2,
  Plus,
  Trash2,
  RefreshCw,
  Zap,
  Eye,
  MousePointer,
  Activity,
  BarChart3,
  Play,
  Sparkles,
  ArrowRight,
  X
} from 'lucide-react';
import Modal from '../../ui/Modal';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import VercelConnect from '../../integrations/VercelConnect';
import vercelService from '../../../services/vercelService';
import landingPageService, { VercelDomain } from '../../../services/landingPageService';
import toast from 'react-hot-toast';

interface PublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  pageId: string;
  pageName: string;
  pageSlug: string;
  onSlugChange: (slug: string) => void;
  onPublish: () => Promise<{ url: string; success: boolean; error?: string }>;
  existingUrl?: string;
  fluxEnabled?: boolean;
}

type DeployStatus = 'idle' | 'building' | 'ready' | 'error';

const PublishModal: React.FC<PublishModalProps> = ({
  isOpen,
  onClose,
  pageId,
  pageName,
  pageSlug,
  onSlugChange,
  onPublish,
  existingUrl,
  fluxEnabled = false
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<DeployStatus>('idle');
  const [deployedUrl, setDeployedUrl] = useState<string | null>(existingUrl || null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [vercelConnected, setVercelConnected] = useState<boolean | null>(null);
  const [checkingVercel, setCheckingVercel] = useState(true);

  // Domain management state
  const [domains, setDomains] = useState<VercelDomain[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);

  // DNS info toggle for verified domains
  const [showDnsFor, setShowDnsFor] = useState<string | null>(null);

  // Flux Analytics state - only show promo if Flux is not already configured
  const [showFluxPromo, setShowFluxPromo] = useState(!fluxEnabled);
  const [fluxExpanded, setFluxExpanded] = useState(false);

  // Check Vercel status when modal opens
  useEffect(() => {
    const checkVercel = async () => {
      if (isOpen) {
        setCheckingVercel(true);
        try {
          const response = await vercelService.getStatus();
          setVercelConnected(response.data?.connected || false);
        } catch (error) {
          setVercelConnected(false);
        } finally {
          setCheckingVercel(false);
        }
      }
    };

    checkVercel();
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // If page is already published, show as ready so domain section appears
      setStatus(existingUrl ? 'ready' : 'idle');
      setError(null);
      setDeployedUrl(existingUrl || null);
      setDomains([]);
      setNewDomain('');
      // Only show Flux promo if not already configured
      setShowFluxPromo(!fluxEnabled);

      // If already published, load domains
      if (existingUrl && pageId) {
        loadDomains();
      }
    }
  }, [isOpen, existingUrl, pageId, fluxEnabled]);

  // Load domains for the page
  const loadDomains = async () => {
    if (!pageId) return;

    setLoadingDomains(true);
    try {
      const response = await landingPageService.getDomains(pageId);
      if (response.success && response.data?.domains) {
        // Normalize domains - Vercel API may return different field names
        const normalizedDomains = response.data.domains.map((d: any) => ({
          name: d.name || d.domain,
          // Vercel uses 'verified' or check if verification array is empty
          verified: d.verified === true || (d.verification && d.verification.length === 0),
          configured: d.configured,
          verification: d.verification
        }));
        console.log('Loaded domains:', normalizedDomains);
        setDomains(normalizedDomains);
      }
    } catch (error) {
      console.error('Error loading domains:', error);
    } finally {
      setLoadingDomains(false);
    }
  };

  // Add a custom domain
  const handleAddDomain = async () => {
    if (!newDomain.trim() || !pageId) return;

    const domainToAdd = newDomain.trim().toLowerCase();

    // Basic domain validation
    const domainRegex = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    if (!domainRegex.test(domainToAdd)) {
      toast.error(t('pageBuilder.publish.invalidDomain', 'Invalid domain. Example: landing.yoursite.com'));
      return;
    }

    setAddingDomain(true);
    try {
      const response = await landingPageService.addDomain(pageId, domainToAdd);
      if (response.success) {
        toast.success(t('pageBuilder.publish.domainAdded', 'Domain added! Configure DNS.'));
        // Add domain immediately to show DNS instructions
        setDomains(prev => [...prev, { name: domainToAdd, verified: false }]);
        setNewDomain('');
        // Don't auto-reload - let user click refresh manually after configuring DNS
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      toast.error(error.message || t('pageBuilder.publish.domainError', 'Error adding domain'));
    } finally {
      setAddingDomain(false);
    }
  };

  // Remove a domain
  const handleRemoveDomain = async (domain: string) => {
    if (!pageId) return;
    if (!window.confirm(t('pageBuilder.publish.confirmRemoveDomain', 'Remover o dominio {{domain}}?').replace('{{domain}}', domain))) return;

    try {
      const response = await landingPageService.removeDomain(pageId, domain);
      if (response.success) {
        toast.success(t('pageBuilder.publish.domainRemoved', 'Domain removed'));
        setDomains(prev => prev.filter(d => d.name !== domain));
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      toast.error(error.message || t('pageBuilder.publish.domainRemoveError', 'Error removing domain'));
    }
  };

  const handlePublish = async () => {
    setStatus('building');
    setError(null);

    try {
      const result = await onPublish();

      if (result.success) {
        setDeployedUrl(result.url);
        setStatus('ready');
        // Load domains after successful publish
        setTimeout(() => loadDomains(), 1000);
      } else {
        setError(result.error || t('pageBuilder.publish.errorRetry', 'Erro ao publicar. Tente novamente.'));
        setStatus('error');
      }
    } catch (err) {
      setError(t('pageBuilder.publish.unexpectedError', 'Erro inesperado ao publicar. Tente novamente.'));
      setStatus('error');
    }
  };

  const handleCopyUrl = async () => {
    if (!deployedUrl) return;

    try {
      await navigator.clipboard.writeText(deployedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const slugToUrl = (slug: string) => `https://${slug}.vercel.app`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('pageBuilder.publish.title', 'Publicar Landing Page')}
      size="lg"
    >
      <div className="space-y-6">
        {/* Vercel Connection Check */}
        {checkingVercel ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : !vercelConnected ? (
          <VercelConnect compact onStatusChange={setVercelConnected} />
        ) : null}

        {/* Page Info - only show if Vercel is connected */}
        {vercelConnected && (
          <>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 sm:p-4">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-white truncate">{pageName}</h3>
                  <div className="mt-2">
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                      {t('pageBuilder.publish.pageSlug', 'URL da pagina')}
                    </label>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                      <span className="text-gray-500 dark:text-gray-400 text-sm">https://</span>
                      <Input
                        value={pageSlug}
                        onChange={(e) => onSlugChange(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                        className="flex-1"
                        placeholder="minha-landing-page"
                        disabled={status === 'building'}
                      />
                      <span className="text-gray-500 dark:text-gray-400 text-sm">.vercel.app</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Display */}
            {status === 'building' && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                  <div>
                    <p className="font-medium text-blue-900 dark:text-blue-100">{t('pageBuilder.publish.publishing', 'Publicando...')}</p>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {t('pageBuilder.publish.deployingVercel', 'Publicando na Vercel...')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {status === 'ready' && deployedUrl && (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 overflow-hidden">
                <div className="flex items-start gap-3 min-w-0">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-green-900 dark:text-green-100">{t('pageBuilder.publish.success', 'Publicada com sucesso!')}</p>
                    <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                      {t('pageBuilder.publish.successDesc', 'Sua landing page esta no ar e pronta para receber visitantes')}
                    </p>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-2 border border-green-200 dark:border-green-700 overflow-hidden">
                      <div className="flex items-center gap-2 min-w-0">
                        <Link2 className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-sm text-gray-900 dark:text-white truncate min-w-0 flex-1">{deployedUrl}</span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={handleCopyUrl}
                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            title={t('pageBuilder.publish.copyUrl', 'Copiar URL')}
                          >
                            {copied ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-gray-500" />
                            )}
                          </button>
                          <a
                            href={deployedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                            title={t('pageBuilder.publish.visitPage', 'Visitar Pagina')}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-4 h-4 text-gray-500" />
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* 🚀 FLUX ANALYTICS - ALWAYS VISIBLE PROMOTION */}
            {showFluxPromo && (
              <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-600 p-1 animate-glow">
                {/* Animated background effects */}
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIyIi8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />

                <div className="relative bg-gray-900/95 rounded-xl p-5">
                  {/* Close button */}
                  <button
                    onClick={() => setShowFluxPromo(false)}
                    className="absolute top-3 right-3 p-1 hover:bg-white/10 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>

                  {/* Header with animated badge */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="relative">
                      <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
                        <Zap className="w-7 h-7 text-white" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                        <Sparkles className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-white">Flux Analytics</h3>
                        <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full">
                          {t('pageBuilder.flux.new', 'NOVO')}
                        </span>
                      </div>
                      <p className="text-purple-200 text-sm">
                        {t('pageBuilder.flux.tagline', 'Descubra como visitantes interagem com sua página')}
                      </p>
                    </div>
                  </div>

                  {/* Animated question */}
                  <div className="bg-white/5 rounded-xl p-4 mb-4 border border-white/10">
                    <p className="text-white font-medium mb-2 flex items-center gap-2">
                      <Play className="w-4 h-4 text-fuchsia-400" />
                      {t('pageBuilder.flux.question', 'O que acontece quando alguém visita sua landing page?')}
                    </p>
                    <p className="text-gray-300 text-sm">
                      {t('pageBuilder.flux.answer', 'Com o Flux, você vê EXATAMENTE: onde clicam, quanto tempo ficam, se preenchem formulários, e gravações de sessões reais.')}
                    </p>
                  </div>

                  {/* Feature grid - compact */}
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                      <Eye className="w-4 h-4 text-purple-400" />
                      <span className="text-white text-xs font-medium">{t('pageBuilder.flux.feature1', 'Gravação de Sessões')}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                      <MousePointer className="w-4 h-4 text-blue-400" />
                      <span className="text-white text-xs font-medium">{t('pageBuilder.flux.feature2', 'Heatmaps de Clique')}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                      <Activity className="w-4 h-4 text-green-400" />
                      <span className="text-white text-xs font-medium">{t('pageBuilder.flux.feature3', 'Core Web Vitals')}</span>
                    </div>
                    <div className="flex items-center gap-2 p-2 bg-white/5 rounded-lg">
                      <BarChart3 className="w-4 h-4 text-orange-400" />
                      <span className="text-white text-xs font-medium">{t('pageBuilder.flux.feature4', 'Eventos de Formulário')}</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="flex gap-2">
                    <a
                      href="https://flux.devskin.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-violet-500/25 hover:shadow-violet-500/40"
                    >
                      <Zap className="w-4 h-4" />
                      {t('pageBuilder.flux.cta', 'Ativar Flux Grátis')}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>

                  {/* Trust badges */}
                  <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-white/10">
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-400" />
                      {t('pageBuilder.flux.badge1', 'Setup em 30 segundos')}
                    </span>
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-400" />
                      {t('pageBuilder.flux.badge2', 'Zero impacto no SEO')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-red-900 dark:text-red-100">{t('pageBuilder.publish.error', 'Erro ao publicar')}</p>
                    <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Domain Manager */}
            {status === 'ready' && (
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    {t('pageBuilder.publish.customDomain', 'Dominio Personalizado')}
                  </h4>
                  {loadingDomains ? (
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  ) : (
                    <button
                      onClick={loadDomains}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      title={t('common.refresh', 'Atualizar')}
                    >
                      <RefreshCw className="w-4 h-4 text-gray-500" />
                    </button>
                  )}
                </div>

                {/* Add Domain Form */}
                <div className="flex gap-2 mb-4 min-w-0">
                  <div className="flex-1 min-w-0">
                    <Input
                      placeholder="landing.seusite.com.br"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value.toLowerCase())}
                      disabled={addingDomain}
                    />
                  </div>
                  <Button
                    onClick={handleAddDomain}
                    disabled={!newDomain.trim() || addingDomain}
                    size="sm"
                    className="flex-shrink-0"
                    leftIcon={addingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  >
                    {t('common.add', 'Adicionar')}
                  </Button>
                </div>

                {/* Domain List */}
                {domains.length > 0 ? (
                  <div className="space-y-2">
                    {domains.map((domain) => (
                      <React.Fragment key={domain.name}>
                        <div
                          className="flex items-center justify-between gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg min-w-0"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Globe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <span className="text-sm font-medium text-gray-900 dark:text-white truncate min-w-0">
                              {domain.name}
                            </span>
                            {domain.verified ? (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded flex-shrink-0">
                                {t('pageBuilder.publish.verified', 'Verificado')}
                              </span>
                            ) : (
                              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded flex-shrink-0">
                                {t('pageBuilder.publish.awaitingDns', 'Aguardando DNS')}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {domain.verified && (
                              <button
                                onClick={() => setShowDnsFor(showDnsFor === domain.name ? null : domain.name)}
                                className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                                title={t('pageBuilder.publish.showDns', 'Ver configuração DNS')}
                              >
                                <Eye className="w-4 h-4 text-gray-500" />
                              </button>
                            )}
                            <a
                              href={`https://${domain.name}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-4 h-4 text-gray-500" />
                            </a>
                            <button
                              onClick={() => handleRemoveDomain(domain.name)}
                              className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        {/* DNS info for verified domain */}
                        {domain.verified && showDnsFor === domain.name && (
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm">
                            <p className="text-blue-800 dark:text-blue-200 font-medium mb-2">
                              {t('pageBuilder.publish.dnsConfig', 'Configuração DNS:')}
                            </p>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-700">
                              <div className="grid grid-cols-3 gap-1 sm:gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                                <span>{t('pageBuilder.publish.dnsType', 'Tipo')}</span>
                                <span>{t('pageBuilder.publish.dnsName', 'Nome/Host')}</span>
                                <span>{t('pageBuilder.publish.dnsValue', 'Valor/Destino')}</span>
                              </div>
                              <div className="grid grid-cols-3 gap-1 sm:gap-2 text-sm font-mono py-1">
                                <span className="text-blue-600 dark:text-blue-400 font-semibold">CNAME</span>
                                <span className="text-gray-900 dark:text-white truncate min-w-0" title={domain.name}>
                                  {domain.name}
                                </span>
                                <span className="text-gray-900 dark:text-white text-xs truncate min-w-0">cname.vercel-dns.com</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('pageBuilder.publish.noDomains', 'Nenhum dominio configurado. Adicione seu dominio acima.')}
                  </p>
                )}

                {/* DNS Instructions - Show for unverified domains */}
                {domains.filter(d => !d.verified).length > 0 && (
                  <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {t('pageBuilder.publish.configureDns', 'Configure o DNS no seu provedor:')}
                    </p>

                    <div className="space-y-3">
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        {t('pageBuilder.publish.dnsStep1', 'Acesse o painel de DNS do seu domínio (Cloudflare, GoDaddy, Registro.br, etc.) e adicione:')}
                      </p>

                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-200 dark:border-amber-700">
                        <div className="grid grid-cols-3 gap-1 sm:gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 pb-2 border-b border-gray-200 dark:border-gray-600">
                          <span>{t('pageBuilder.publish.dnsType', 'Tipo')}</span>
                          <span>{t('pageBuilder.publish.dnsName', 'Nome/Host')}</span>
                          <span>{t('pageBuilder.publish.dnsValue', 'Valor/Destino')}</span>
                        </div>
                        {domains.filter(d => !d.verified).map((domain) => (
                          <div key={domain.name} className="grid grid-cols-3 gap-1 sm:gap-2 text-sm font-mono py-1">
                            <span className="text-amber-600 dark:text-amber-400 font-semibold">CNAME</span>
                            <span className="text-gray-900 dark:text-white truncate min-w-0" title={domain.name}>
                              {domain.name}
                            </span>
                            <span className="text-gray-900 dark:text-white text-xs truncate min-w-0">cname.vercel-dns.com</span>
                          </div>
                        ))}
                      </div>

                      <p className="text-xs text-amber-600 dark:text-amber-400">
                        {t('pageBuilder.publish.dnsPropagation', 'A propagação pode levar até 48 horas, mas geralmente acontece em minutos. Clique em atualizar para verificar.')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Preview URL (before publishing) */}
            {status === 'idle' && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {t('pageBuilder.publish.previewUrlLabel', 'Sua pagina sera publicada em:')}
                </p>
                <p className="font-mono text-sm text-indigo-600 dark:text-indigo-400">
                  {slugToUrl(pageSlug || 'minha-landing-page')}
                </p>
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={onClose}
            className="flex-1"
          >
            {status === 'ready' ? t('common.close', 'Fechar') : t('common.cancel', 'Cancelar')}
          </Button>
          {vercelConnected && (
            <Button
              onClick={handlePublish}
              disabled={status === 'building' || !pageSlug}
              className="flex-1"
              leftIcon={status === 'building' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            >
              {status === 'building'
                ? t('pageBuilder.publish.publishing', 'Publicando...')
                : status === 'error'
                  ? t('common.retry', 'Tentar Novamente')
                  : existingUrl
                    ? t('pageBuilder.publish.republish', 'Atualizar')
                    : t('pageBuilder.toolbar.publish', 'Publicar')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default PublishModal;
